import { memo } from "preact/compat";
import { useState } from "preact/hooks";
import { useTranslation } from "react-i18next";
import { ChatPanel, ChatToggle, useIsChatAvailable } from "../../features/chat";
import { useModal } from "../../shared/contexts";
import {
  BetEntry,
  LeaderboardEntry as LeaderboardRow,
} from "../../shared/entries";
import { RoundsEntry } from "../../shared/entries/RoundsEntry";
import {
  BetAvatar,
  ProgressBar,
  TableContainer,
  TableHeader,
  Tabs,
  Typography,
  TableRow,
  TableCell,
  WinAmount,
} from "../../shared/ui";
import { getScoreColor } from "../../shared/utils/get-score-color";
import { useBetFeedStore } from "../../store/betFeedStore";
import css from "./RoundsDetails.module.scss";
import { useRoundsDetails } from "./useRoundsDetails";

const RoundsDetailsComponent = () => {
  const { currentBets, lastRoundX, playersBettingCounters } = useBetFeedStore();
  const { t } = useTranslation();
  // Чат — тогл над табами: открыт, значит вместо табов и их контента.
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isChatAvailable = useIsChatAvailable();
  const isChatVisible = isChatAvailable && isChatOpen;
  const {
    tableHeaders: {
      tableHeader,
      tableRoundsHeader,
      tableBetHeader,
      tabs,
      tabsLeaderboard,
      tabsPeriods,
    },
    activeTab,
    metric,
    period,
    lbLoading,
    lbEntries,
    histLoading,
    histEntries,
    histHasMore,
    loadMore,
    setMetric,
    setPeriod,
    setActiveTab,
  } = useRoundsDetails();

  const { openModal } = useModal();

  const leaderboartHeader =
    metric === "rounds" ? tableRoundsHeader : tableHeader;
  return (
    <div>
      <div className={css.tabsList}>
        {isChatAvailable && (
          <ChatToggle
            className={css.chatToggle}
            isOpen={isChatOpen}
            onToggle={() => setIsChatOpen((prev) => !prev)}
          />
        )}

        {isChatVisible ? (
          <div className={css.chatContent}>
            <ChatPanel />
          </div>
        ) : (
          <>
            <Tabs
              variant="border"
              className={css.mainTab}
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={(id) => setActiveTab(id as any)}
            />
            {activeTab === "leaderboard" && (
              <div className={css.tabContent}>
                <div className={css.tabs}>
                  <Tabs
                    classNameBody={css.firstTabContainer}
                    tabs={tabsLeaderboard}
                    activeTab={metric}
                    onTabChange={(id) => setMetric(id as any)}
                  />
                  <Tabs
                    classNameBody={css.secondTabContainer}
                    tabs={tabsPeriods}
                    activeTab={period}
                    onTabChange={(id) => setPeriod(id as any)}
                  />
                </div>
                <TableContainer className={css.tableContainer}>
                  <TableHeader columns={leaderboartHeader} />
                  <div className={css.betList}>
                    {lbLoading ? (
                      <Typography variant="caption2" color="header-small">
                        Loading leaderboard…
                      </Typography>
                    ) : metric === "rounds" ? (
                      lbEntries.map((e) => (
                        <RoundsEntry key={e.rank} entry={e} />
                      ))
                    ) : (
                      lbEntries.map((e) => (
                        <LeaderboardRow key={e.rank} entry={e} />
                      ))
                    )}
                  </div>
                </TableContainer>
              </div>
            )}
            {activeTab === "my-bets" && (
              <div className={css.tabContent}>
                <div className={css.roundResultWrapper}>
                  <div className={css.roundResult}>
                    <Typography variant="caption2" color="header">
                      {t("rounds_details.round_result")}
                    </Typography>
                    <Typography variant="h1" color={getScoreColor(lastRoundX)}>
                      {lastRoundX}x
                    </Typography>
                  </div>
                </div>
                <TableContainer className={css.tableContainer}>
                  <TableHeader columns={tableBetHeader} />
                  <div className={css.betList}>
                    {histLoading ? (
                      <Typography variant="caption2" color="header-small">
                        Loading my bets…
                      </Typography>
                    ) : (
                      histEntries.map((b, idx) => (
                        <TableRow
                          key={`${b.betId}-${idx}`}
                          className={css.row}
                          onClick={() => openModal("myBetDetails", b.roundId)}
                        >
                          <TableCell>
                            <BetAvatar
                              avatar={`Avatar_1.png`}
                              username={"You"}
                            />
                          </TableCell>
                          <TableCell className={css.cellCenter}>
                            <Typography variant="caption2" color="light-grey">
                              {b.betAmount ?? 0}
                            </Typography>
                          </TableCell>
                          <TableCell className={css.cellCenter}>
                            <Typography variant="caption2" color="grey-black">
                              {b.multiplier != null && b.multiplier > 1
                                ? `${b.multiplier.toFixed(2)}x`
                                : "-"}
                            </Typography>
                          </TableCell>
                          <TableCell className={css.cellEnd}>
                            <WinAmount amount={b.payout ?? null} />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                    {histHasMore ? (
                      <div className={css.loadMoreWrap}>
                        <button
                          className={css.loadMoreBtn}
                          onClick={loadMore}
                          disabled={histLoading}
                        >
                          {t("rounds_details.load_more")}
                        </button>
                      </div>
                    ) : null}
                  </div>
                </TableContainer>
              </div>
            )}
            {activeTab === "all-bets" && (
              <div className={css.tabContent}>
                <div className={css.roundResultWrapper}>
                  <div className={css.roundResultBlock}>
                    <div className={css.avatarsContainer}>
                      {currentBets.slice(0, 3).map((b) => (
                        <BetAvatar
                          key={b.id}
                          className={css.avatar}
                          size={34}
                          avatar={`${b.avatar}.png`}
                        />
                      ))}
                    </div>
                    <div className={css.progressBar}>
                      <ProgressBar
                        value={playersBettingCounters.cashedOutCount ?? 0}
                        max={playersBettingCounters.totalCount ?? 0}
                      />
                      <div className={css.progressBarText}>
                        <Typography variant="tab-select" color="header-small">
                          {playersBettingCounters.cashedOutCount} /{" "}
                          {playersBettingCounters.totalCount}
                        </Typography>
                        <Typography variant="caption1" color="header-small">
                          {t("rounds_details.all_bets")}
                        </Typography>
                      </div>
                    </div>
                    <div className={css.roundsCashoutResult}>
                      <Typography variant="h1" color={"white"}>
                        {playersBettingCounters.totalCashedOutAmount?.toFixed(
                          2
                        )}
                        $
                      </Typography>
                      <Typography variant="caption1" color="header-small">
                        {t("rounds_details.round_result")}
                      </Typography>
                    </div>
                  </div>
                </div>

                <TableContainer className={css.tableContainer}>
                  <TableHeader columns={tableBetHeader} />
                  <div className={css.betList}>
                    {currentBets.map((bet) => (
                      <BetEntry key={bet.id} bet={bet} />
                    ))}
                  </div>
                </TableContainer>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const RoundsDetails = memo(RoundsDetailsComponent);
